<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { appContainer } from "../../container";
import { InvitedUserInterface } from "../../interface/UserInterface";
import { LoadingService } from "../../services/loadingService";
import { UserService } from "../../services/userService";
import UserCardRaterParticipant from "../users/components/UserCardRaterParticipant.vue";

type SessionTab = "active" | "recent";

const route = useRoute();
const isLoading = ref(true);
const loadingService = appContainer.resolve(LoadingService);
const userService = appContainer.resolve(UserService);

// The new dedicated endpoints return the full list (no pagination), so we
// keep it all in memory and filter on the client.
const allSessions = ref<InvitedUserInterface[]>([]);
const searchField = ref("");

// Tab is pre-selected from the dashboard card link (?tab=active|recent).
const activeTab = ref<SessionTab>(
  route.query.tab === "recent" ? "recent" : "active",
);

// Client-side search over the fetched list (participant name + email).
const filteredSessions = computed(() => {
  const term = searchField.value.trim().toLowerCase();
  if (!term) {
    return allSessions.value;
  }
  return allSessions.value.filter((session) => {
    const user = session.invited_user;
    const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`
      .trim()
      .toLowerCase();
    const email = (user?.email ?? "").toLowerCase();
    return fullName.includes(term) || email.includes(term);
  });
});

onMounted(async () => {
  await getSessions().then(() => (isLoading.value = false));
});

async function getSessions() {
  const loader = await loadingService.show();
  await (activeTab.value === "active"
    ? userService.getActiveParticipants()
    : userService.getRecentlyCompleted()
  )
    .then((result) => {
      allSessions.value = result;
    })
    .finally(() => loader.hide());
}

function changeTab(tab: SessionTab) {
  if (activeTab.value === tab) {
    return;
  }
  activeTab.value = tab;
  searchField.value = "";
  getSessions();
}
</script>

<template>
  <template v-if="!isLoading">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="fw-medium fs-4 p-0">Assessment Sessions</h2>
    </div>
    <form
      class="d-flex w-100 justify-content-between flex-wrap mb-4"
      @submit.prevent
    >
      <div class="search-wrapper d-flex m-0 position-relative w-100 gap-4">
        <input
          type="search"
          class="form-control"
          placeholder="Search"
          v-model="searchField"
        />
        <button type="submit" class="text-white btn bg-primary flex-center">
          Search
        </button>
      </div>
    </form>
    <ul
      class="nav nav-pills mb-3 justify-content-between justify-content-sm-start"
    >
      <li class="nav-item">
        <button
          class="nav-link"
          @click="changeTab('active')"
          :class="{ active: activeTab === 'active' }"
        >
          Active Sessions
        </button>
      </li>
      <li class="nav-item">
        <button
          class="nav-link"
          @click="changeTab('recent')"
          :class="{ active: activeTab === 'recent' }"
        >
          Recently Completed
        </button>
      </li>
    </ul>
    <div v-if="filteredSessions.length">
      <UserCardRaterParticipant
        v-for="user in filteredSessions"
        :key="user.id"
        :user="user"
      />
    </div>
    <div
      class="no-result d-flex align-items-center justify-content-center text-opacity-50 text-danger"
      v-else
    >
      No Records Found
    </div>
  </template>
</template>

<style scoped lang="scss">
.search-wrapper {
  max-width: 350px;
}

.nav-link {
  border-radius: 0;
  border-bottom: 3px solid transparent;
  &.active {
    background-color: transparent;
    color: var(--bs-primary);
    border-color: var(--bs-primary);
  }
}

@media (max-width: 575px) {
  .nav-link {
    font-size: 14px;
  }
}
</style>
